import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2 } from 'lucide-react';
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-119">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-120">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-121">Patient File</h1>
          <div className="flex space-x-2" data-id="element-122">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-123">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-124">
              <Printer className="w-4 h-4 mr-2" data-id="element-125" /> Print / Download PDF
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-126">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-127" /> Delete File
              </Button>}
          </div>
        </div>

        {/* All Encounters – Always Expanded */}
        {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 ${index > 0 ? 'print:break-before-page' : ''}`} data-id="element-128">
              {/* ═══ FORM HEADER ═══ */}
              <div className="flex justify-between items-start mb-6" data-id="element-129">
                <div className="flex items-center" data-id="element-130">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-131" />
                  <div data-id="element-132">
                    <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-133">
                      BMED Medical Centre
                    </h1>
                    <p className="text-sm italic text-gray-600" data-id="element-134">
                      Maternity Care Made Better
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-135">
                  <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-136">
                    Confidential
                  </p>
                  <p className="text-sm" data-id="element-137">
                    <span className="font-semibold" data-id="element-138">Date:</span>{' '}
                    {new Date(enc.startedAt).toLocaleDateString()}
                  </p>
                  <p className="text-sm" data-id="element-139">
                    <span className="font-semibold" data-id="element-140">File No:</span>{' '}
                    {selectedPatient.bmcFileNumber}
                  </p>
                </div>
              </div>

              {/* ═══ PERSONAL DETAILS ═══ */}
              <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-141">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-142">
                  <div className="col-span-1 font-semibold" data-id="element-143">1.</div>
                  <div className="col-span-6" data-id="element-144">
                    <span className="font-semibold" data-id="element-145">Full Names:</span>{' '}
                    {selectedPatient.firstName} {selectedPatient.lastName}
                  </div>
                  <div className="col-span-3" data-id="element-146">
                    <span className="font-semibold" data-id="element-147">Sex:</span>{' '}
                    {selectedPatient.sex}
                  </div>
                  <div className="col-span-2" data-id="element-148">
                    <span className="font-semibold" data-id="element-149">Age:</span>{' '}
                    {selectedPatient.age}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-150">
                  <div className="col-span-1 font-semibold" data-id="element-151">2.</div>
                  <div className="col-span-11" data-id="element-152">
                    <span className="font-semibold" data-id="element-153">Marital Status:</span>{' '}
                    {selectedPatient.maritalStatus}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-154">
                  <div className="col-span-1 font-semibold" data-id="element-155">3.</div>
                  <div className="col-span-7" data-id="element-156">
                    <span className="font-semibold" data-id="element-157">Home Address:</span>{' '}
                    {selectedPatient.address}
                  </div>
                  <div className="col-span-4" data-id="element-158">
                    <span className="font-semibold" data-id="element-159">Contact:</span>{' '}
                    {selectedPatient.contact}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-160">
                  <div className="col-span-1 font-semibold" data-id="element-161">4.</div>
                  <div className="col-span-11" data-id="element-162">
                    <span className="font-semibold" data-id="element-163">Occupation:</span>{' '}
                    {selectedPatient.occupation || '_________________________'}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-164">
                  <div className="col-span-1 font-semibold" data-id="element-165">5.</div>
                  <div className="col-span-5" data-id="element-166">
                    <span className="font-semibold" data-id="element-167">Next of Kin:</span>{' '}
                    {selectedPatient.nextOfKin}
                  </div>
                  <div className="col-span-3" data-id="element-168">
                    <span className="font-semibold" data-id="element-169">Contact:</span>{' '}
                    {selectedPatient.nokContact}
                  </div>
                  <div className="col-span-3" data-id="element-170">
                    <span className="font-semibold" data-id="element-171">Relationship:</span>{' '}
                    {selectedPatient.nokRelationship}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-172">
                  <div className="col-span-1 font-semibold" data-id="element-173">6.</div>
                  <div className="col-span-11" data-id="element-174">
                    <span className="font-semibold" data-id="element-175">Allergies:</span>{' '}
                    <span className="text-red-600 font-medium" data-id="element-176">
                      {selectedPatient.allergies}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2" data-id="element-177">
                  <div className="col-span-1 font-semibold" data-id="element-178">7.</div>
                  <div className="col-span-11" data-id="element-179">
                    <span className="font-semibold" data-id="element-180">HIV Status:</span>{' '}
                    <span className="ml-4" data-id="element-181">
                      <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-182">
                        Positive{' '}
                        {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                      </span>
                      <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-183">
                        Negative{' '}
                        {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                      </span>
                      <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-184">
                        Unknown{' '}
                        {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* ═══ NURSING OBSERVATIONS ═══ */}
              <div className="mb-6" data-id="element-185">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-186">
                  Nursing Observations
                </h3>
                <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-187">
                  <thead data-id="element-188">
                    <tr className="bg-gray-50" data-id="element-189">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-190">
                          {n}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-191">
                      <th className="border border-black p-1" data-id="element-192">Temp</th>
                      <th className="border border-black p-1" data-id="element-193">Pulse</th>
                      <th className="border border-black p-1" data-id="element-194">Resp</th>
                      <th className="border border-black p-1" data-id="element-195">BP</th>
                      <th className="border border-black p-1" data-id="element-196">SpO2</th>
                      <th className="border border-black p-1" data-id="element-197">Weight (Kg)</th>
                      <th className="border border-black p-1" data-id="element-198">Height</th>
                      <th className="border border-black p-1" data-id="element-199">BMI</th>
                      <th className="border border-black p-1" data-id="element-200">Pain Score</th>
                      <th className="border border-black p-1" data-id="element-201">AVPU</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-202">
                    <tr data-id="element-203">
                      <td className="border border-black p-2 font-medium" data-id="element-204">
                        {enc.vitals?.temp || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-205">
                        {enc.vitals?.pulse || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-206">
                        {enc.vitals?.resp || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-207">
                        {enc.vitals?.bp || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-208">
                        {enc.vitals?.sp02 || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-209">
                        {enc.vitals?.weight || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-210">
                        {enc.vitals?.height || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-211">
                        {enc.vitals?.bmi || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-212">
                        {enc.vitals?.painScore || ''}
                      </td>
                      <td className="border border-black p-2 font-medium" data-id="element-213">
                        {enc.vitals?.avpu || ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ═══ CLINICAL NOTES ═══ */}
              <div className="space-y-5 mb-6 text-sm" data-id="element-214">
                <div className="border-2 border-black" data-id="element-215">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-216">
                    Presenting Complaint
                  </h3>
                  <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-217">
                    {enc.clinicalNote?.presentingComplaint || ''}
                  </div>
                </div>

                <div className="border-2 border-black" data-id="element-218">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-219">
                    History of Presenting Complaint
                  </h3>
                  <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-220">
                    {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                  </div>
                </div>

                {/* PAST MEDICAL HISTORY */}
                <div className="border-2 border-black" data-id="element-221">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-222">
                    Past Medical History / General Co-Morbidities
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-223">
                    <tbody data-id="element-224">
                      {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-225">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-226">
                            {condition}
                          </td>
                          <td className="p-2 text-center" data-id="element-227">
                            {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* DRUG HISTORY */}
                <div className="border-2 border-black" data-id="element-228">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-229">
                    Drug History
                  </h3>
                  <div className="p-3 min-h-[40px]" data-id="element-230">
                    {enc.clinicalNote?.drugHistory || ''}
                  </div>
                  <div className="p-3 border-t border-gray-300" data-id="element-231">
                    <span className="font-semibold" data-id="element-232">Any known allergy:</span>{' '}
                    <span className="text-red-600 font-medium" data-id="element-233">
                      {selectedPatient.allergies}
                    </span>
                  </div>
                </div>

                {/* FAMILY MEDICAL HISTORY */}
                <div className="border-2 border-black" data-id="element-234">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-235">
                    Family Medical History
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-236">
                    <tbody data-id="element-237">
                      {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-238">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-239">
                            {condition}
                          </td>
                          <td className="p-2 text-center" data-id="element-240">
                            {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                <div className="border-2 border-black" data-id="element-241">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-242">
                    Obstetrics and Gynaecology History
                  </h3>
                  <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-243">
                    {enc.clinicalNote?.obgynHistory || ''}
                  </div>
                </div>

                {/* SOCIAL HISTORY */}
                <div className="border-2 border-black" data-id="element-244">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-245">
                    Social History
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-246">
                    <tbody data-id="element-247">
                      <tr className="border-b border-gray-300" data-id="element-248">
                        <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-249">
                          Alcohol
                        </td>
                        <td className="p-2" data-id="element-250">
                          {enc.clinicalNote?.socialHistory?.alcohol || ''}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300" data-id="element-251">
                        <td className="border-r border-gray-300 p-2 font-medium" data-id="element-252">
                          Smoking
                        </td>
                        <td className="p-2" data-id="element-253">
                          {enc.clinicalNote?.socialHistory?.smoking || ''}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300" data-id="element-254">
                        <td className="border-r border-gray-300 p-2 font-medium" data-id="element-255">
                          Occupation
                        </td>
                        <td className="p-2" data-id="element-256">
                          {enc.clinicalNote?.socialHistory?.occupation || ''}
                        </td>
                      </tr>
                      <tr data-id="element-257">
                        <td className="border-r border-gray-300 p-2 font-medium" data-id="element-258">
                          Mental Status
                        </td>
                        <td className="p-2" data-id="element-259">
                          {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* REVIEW OF SYSTEMS */}
                <div className="border-2 border-black" data-id="element-260">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-261">
                    Review of Systems
                  </h3>
                  <div className="p-3 space-y-1" data-id="element-262">
                    <p data-id="element-263">
                      <span className="font-semibold" data-id="element-264">R/S:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                    </p>
                    <p data-id="element-265">
                      <span className="font-semibold" data-id="element-266">CVS:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                    </p>
                    <p data-id="element-267">
                      <span className="font-semibold" data-id="element-268">CNS:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                    </p>
                    <p data-id="element-269">
                      <span className="font-semibold" data-id="element-270">GIT:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.git || ''}
                    </p>
                    <p data-id="element-271">
                      <span className="font-semibold" data-id="element-272">GUT:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                    </p>
                    <p data-id="element-273">
                      <span className="font-semibold" data-id="element-274">MSS:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                    </p>
                    <p data-id="element-275">
                      <span className="font-semibold" data-id="element-276">IS:</span>{' '}
                      {enc.clinicalNote?.reviewOfSystems?.is || ''}
                    </p>
                  </div>
                </div>

                {/* ON EXAMINATION */}
                <div className="border-2 border-black" data-id="element-277">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-278">
                    On Examination
                  </h3>
                  <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-279">
                    {enc.clinicalNote?.onExamination || 'G/C – Stable / Ill Looking / Unconscious\n- Febrile / Afebrile / Lethargic\n- P|J|C\n\nS1  S2 : -\n\nR/S  : -\n\nP/A'}
                  </div>
                </div>

                {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                <div className="border-2 border-black" data-id="element-280">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-281">
                    Summary of Significant Findings
                  </h3>
                  <div className="p-3 min-h-[60px]" data-id="element-282">
                    {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-283">
                        {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-284">{finding}</li>)}
                      </ol> : <div className="space-y-2 text-gray-400" data-id="element-285">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-286">
                            {n}.
                            ______________________________________________________________________________________
                          </p>)}
                      </div>}
                  </div>
                </div>

                {/* WORKING DIAGNOSIS */}
                <div className="border-2 border-black" data-id="element-287">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-288">
                    Working Diagnosis
                  </h3>
                  <div className="p-3 min-h-[60px]" data-id="element-289">
                    {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-290">
                        {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-291">
                              {dx}
                            </li>)}
                      </ol> : <div className="space-y-2 text-gray-400" data-id="element-292">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-293">
                            {n}.
                            ______________________________________________________________________________________
                          </p>)}
                      </div>}
                  </div>
                </div>

                {/* INVESTIGATIONS TO BE DONE */}
                <div className="border-2 border-black" data-id="element-294">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-295">
                    Investigations To Be Done
                  </h3>
                  <div className="p-4" data-id="element-296">
                    <table className="w-full border-collapse text-sm mb-4" data-id="element-297">
                      <tbody data-id="element-298">
                        {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATINE / U&A', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['LIVER FUNCTION TEST', 'DOA 6'], ['CXR', 'RPR'], ['HIV', 'SCAN/ULTRA SOUND'], ['SPERM ANALYSIS', 'TESTOSTERONE']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-299">
                            {pair.map(test => {
                      const isOrdered = enc.labRequests.some(lab => lab.tests.includes(test)) || test === 'SCAN/ULTRA SOUND' && enc.radiologyRequests.length > 0;
                      return <Fragment key={test} data-id="element-300">
                                  <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-301">
                                    {test}
                                  </td>
                                  <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-302">
                                    {isOrdered ? <span className="text-green-700 font-bold" data-id="element-303">
                                        ✓
                                      </span> : ''}
                                  </td>
                                </Fragment>;
                    })}
                          </tr>)}
                      </tbody>
                    </table>

                    <div className="border-t border-gray-300 pt-2" data-id="element-304">
                      <span className="font-semibold" data-id="element-305">
                        Other investigations:
                      </span>{' '}
                      {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                    </div>

                    {/* Investigation Results */}
                    {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-306">
                        <h4 className="font-bold mb-3 uppercase text-center" data-id="element-307">
                          Investigation Results
                        </h4>

                        {enc.labRequests.filter(l => l.status === 'Completed' && l.results).map(lab => <table key={lab.id} className="w-full border-collapse text-sm mb-3" data-id="element-308">
                              <tbody data-id="element-309">
                                {Object.entries(lab.results!).map(([t, r]) => <tr key={t} className="border-b border-gray-200" data-id="element-310">
                                    <td className="p-1.5 font-medium w-1/3 border-r border-gray-200" data-id="element-311">
                                      {t}
                                    </td>
                                    <td className="p-1.5" data-id="element-312">{r}</td>
                                  </tr>)}
                              </tbody>
                            </table>)}

                        {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <div key={rad.id} className="mb-4 border border-gray-300 rounded p-3" data-id="element-313">
                              <p className="font-bold mb-1" data-id="element-314">{rad.scanType}</p>
                              {rad.radiographerName && <p className="text-xs text-gray-500 mb-2" data-id="element-315">
                                  Radiographer: {rad.radiographerName}
                                </p>}
                              {rad.report && <div className="mb-2" data-id="element-316">
                                  <span className="font-semibold" data-id="element-317">Report:</span>
                                  <p className="whitespace-pre-wrap mt-1" data-id="element-318">
                                    {rad.report}
                                  </p>
                                </div>}
                              {rad.images && rad.images.length > 0 && <div data-id="element-319">
                                  <span className="font-semibold" data-id="element-320">
                                    Scan Images:
                                  </span>
                                  <div className="flex gap-3 mt-2 overflow-x-auto print:flex-wrap" data-id="element-321">
                                    {rad.images.map((img, idx) => <img key={idx} src={img} alt={`${rad.scanType} ${idx + 1}`} className="h-28 w-28 object-cover rounded border-2 border-gray-300 cursor-pointer hover:border-blue-500 transition-colors print:h-36 print:w-36" onClick={() => window.open(img, '_blank')} data-id="element-322" />)}
                                  </div>
                                </div>}
                            </div>)}
                      </div>}
                  </div>
                </div>

                {/* DRUG TREATMENT / PRESCRIPTION */}
                <div className="border-2 border-black" data-id="element-323">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-324">
                    Drug Treatment / Prescription
                  </h3>
                  <table className="w-full border-collapse text-sm text-center" data-id="element-325">
                    <thead data-id="element-326">
                      <tr className="bg-gray-100" data-id="element-327">
                        <th className="border border-black p-1.5 w-10" data-id="element-328">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-329">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5" data-id="element-330">Dose</th>
                        <th className="border border-black p-1.5" data-id="element-331">Route</th>
                        <th className="border border-black p-1.5" data-id="element-332">Frequency</th>
                        <th className="border border-black p-1.5" data-id="element-333">
                          No. of Days
                        </th>
                        <th className="border border-black p-1.5" data-id="element-334">Signature</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-335">
                      {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-336">
                              <td className="border border-black p-1.5" data-id="element-337">
                                {i + 1}
                              </td>
                              <td className="border border-black p-1.5 text-left font-medium" data-id="element-338">
                                {rx.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-339">
                                {rx.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-340">
                                {rx.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-341">
                                {rx.frequency}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-342">
                                {rx.days}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-343">
                                {enc.lastAttendingClinician || ''}
                              </td>
                            </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-344">
                              <td className="border border-black p-3" data-id="element-345">{i}</td>
                              <td className="border border-black p-3" data-id="element-346"></td>
                              <td className="border border-black p-3" data-id="element-347"></td>
                              <td className="border border-black p-3" data-id="element-348"></td>
                              <td className="border border-black p-3" data-id="element-349"></td>
                              <td className="border border-black p-3" data-id="element-350"></td>
                              <td className="border border-black p-3" data-id="element-351"></td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* FOLLOW UP / REVIEW DATE */}
                <div className="border-2 border-black" data-id="element-352">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-353">
                    Next Follow Up / Review Date (If Any)
                  </h3>
                  <table className="w-full border-collapse text-sm text-center" data-id="element-354">
                    <thead data-id="element-355">
                      <tr className="bg-gray-100" data-id="element-356">
                        <th className="border border-black p-1 w-10" data-id="element-357">No.</th>
                        <th className="border border-black p-1" data-id="element-358">Date</th>
                        <th className="border border-black p-1 w-10" data-id="element-359">No.</th>
                        <th className="border border-black p-1" data-id="element-360">Date</th>
                        <th className="border border-black p-1 w-10" data-id="element-361">No.</th>
                        <th className="border border-black p-1" data-id="element-362">Date</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-363">
                      {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-364">
                          <td className="border border-black p-1 font-medium" data-id="element-365">
                            {row + 1}
                          </td>
                          <td className="border border-black p-2" data-id="element-366">
                            {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                          </td>
                          <td className="border border-black p-1 font-medium" data-id="element-367">
                            {row + 6}
                          </td>
                          <td className="border border-black p-2" data-id="element-368"></td>
                          <td className="border border-black p-1 font-medium" data-id="element-369">
                            {row + 11}
                          </td>
                          <td className="border border-black p-2" data-id="element-370"></td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* NAME OF MEDICAL PRACTITIONER */}
                <div className="border-2 border-black p-4 mt-6" data-id="element-371">
                  <p className="font-bold text-sm" data-id="element-372">
                    Name of the Medical Practitioner:{' '}
                    <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-373">
                      {enc.lastAttendingClinician || ''}
                    </span>
                  </p>
                </div>
              </div>
            </div>) : <Card data-id="element-374">
            <p className="text-gray-500 text-center py-8" data-id="element-375">
              No encounters found for this patient.
            </p>
          </Card>}
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-376">
      <div className="flex justify-between items-center" data-id="element-377">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-378">Patient Directory</h1>
      </div>

      <Card data-id="element-379">
        <div className="relative" data-id="element-380">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-381">
            <Search className="h-5 w-5 text-gray-400" data-id="element-382" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-383" />
        </div>
      </Card>

      <Card noPadding data-id="element-384">
        <div className="overflow-x-auto" data-id="element-385">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-386">
            <thead className="bg-gray-50" data-id="element-387">
              <tr data-id="element-388">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-389">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-390">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-391">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-392">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-393">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-394">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-395">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-396">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-397">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-398">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-399">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-400">
                      <Button variant="outline" size="sm" onClick={() => handleSelectPatient(patient)} data-id="element-401">
                        <FileText className="w-4 h-4 mr-2" data-id="element-402" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-403">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-404">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};